import type { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/toast';
import { useActiveCompany, useSwitchActiveCompany } from '@/lib/active-company-context';

/** Detalle de un documento: pertenece a una empresa, no tiene sentido tras cambiar. */
const DOCUMENT_DETAIL = /^\/documentos\/[^/]+/;

/**
 * Empresa activa del header. Con varias empresas es el Select del sistema; con una
 * sola, solo se muestra. Sin empresas (o si /me falló) se dice con una nota simple,
 * que no parece un selector.
 */
export function CompanySwitcher(): ReactElement {
  const { activeCompanyId, activeCompanyName, companies, isLoading, isError } = useActiveCompany();
  const switchTo = useSwitchActiveCompany();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!isLoading && activeCompanyId === null && (isError || companies.length === 0)) {
    return (
      <div className="company">
        <span className="company-note">
          {isError ? 'Empresa no disponible' : 'Sin empresa asignada'}
        </span>
      </div>
    );
  }

  const name = activeCompanyName ?? 'Empresa';

  if (companies.length < 2 || activeCompanyId === null) {
    return (
      <div className="company">
        <span className="lab">Empresa activa</span>
        <div className="selector is-static">
          <span className="swatch" />
          {name}
        </div>
      </div>
    );
  }

  function onChange(companyId: string): void {
    switchTo(companyId);
    const chosen = companies.find((c) => c.id === companyId)?.name ?? 'la empresa elegida';
    showToast(`Ahora trabajas en ${chosen}`);
    if (DOCUMENT_DETAIL.test(pathname)) {
      void navigate('/documentos');
    }
  }

  return (
    <div className="company">
      <span className="lab" aria-hidden="true">
        Empresa activa
      </span>
      <Select
        className="sel-inline"
        value={activeCompanyId}
        options={companies.map((c) => ({ value: c.id, label: c.name }))}
        onChange={onChange}
        ariaLabel={`Empresa activa: ${name}`}
        listLabel="Empresa activa"
        leading={<span className="swatch" />}
      />
    </div>
  );
}
